import React, { createContext, ReactNode } from 'react';
import { fetchAllData } from '../data/fetch_data';
import { ALL_DATA } from '../data/all_data';
import { ALL } from 'dns';
import { debounce } from 'lodash';
import axios from 'axios';
import { SelectChangeEvent } from '@mui/material/Select';
import { translateText } from '../services/translation.service';
import { useGetDataFromFirestore } from 'src/hooks/useGetDataFromFirestore.hook';
import FirebaseService from 'src/services/firebase.service';
import { generateUUID } from 'src/utils/number.util';
import { CATEGORIES_LABELS_MAP } from 'src/constants/category.constants';
import { DEFAULT_PRODUCT } from 'src/constants/product.constants';
import { ToastContainer, toast } from 'react-toastify';

interface ProductProviderProps {
    children: ReactNode;
}

interface ProductContextProps {
    products: IProduct[];
    setProducts: React.Dispatch<React.SetStateAction<IProduct[]>>;
    search: (query: string) => void;
    resetSearch: () => void;
    searchedProducts: IProduct[];
    setSearchedProducts: React.Dispatch<React.SetStateAction<IProduct[]>>;
    inputValue: string;
    setInputValue: React.Dispatch<React.SetStateAction<string>>;
    productFields: IEditProduct;
    category: string;
    handleInputChange: (propertyName: string, value: string | number | any[]) => void,
    handleSelectChange: (event: SelectChangeEvent) => void;
    postProduct: () => void;
    deleteAllProducts: () => void;
    deleteProduct: (id: string) => void;
    translatedProducts: any[];
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ProductContext = createContext<ProductContextProps>({
    products: [],
    setProducts: () => {},
    search: () => {},
    resetSearch: () => {},
    searchedProducts: [],
    setSearchedProducts: () => {},
    inputValue: '',
    setInputValue: () => {},
    productFields: DEFAULT_PRODUCT,
    category: '',
    handleSelectChange: () => {},
    handleInputChange: () => {},
    postProduct: () => {},
    deleteAllProducts: () => {},
    deleteProduct: () => {},
    translatedProducts: [],
    isLoading: true,
    setIsLoading: () => {} 
});

export const ProductProvider: React.FC<ProductProviderProps> = ({ children }) => {
    const [products, setProducts] = React.useState<IProduct[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [triggerReload, setTriggerReload] = React.useState(false);
    const [searchedProducts, setSearchedProducts] = React.useState<IProduct[]>([]);
    const [inputValue, setInputValue] = React.useState('');
    const [category, setCategory] = React.useState('');
    const [productFields, setProductFields] = React.useState<IEditProduct>(DEFAULT_PRODUCT);

    const firebaseService = new FirebaseService();

    const { data: prices } = useGetDataFromFirestore('prices');

    React.useEffect(() => {
        if (prices.length > 0) {
            setProducts(prices);
        }
    }, [prices]);

    const handleInputChange = (propertyName: string, value: string | number | any[]) => {
        console.log('handleInputChange:', { propertyName, value });
        setProductFields({
            ...productFields,
            [propertyName]: value,
        });
    };

    const handleSelectChange = (event: SelectChangeEvent) => {
        const selectedCategory = event.target.value;
        setCategory(selectedCategory);
    
        const categoryData = CATEGORIES_LABELS_MAP[selectedCategory];
        const newLabelName = categoryData ? categoryData.label : '';
        const newSizes = categoryData ? categoryData.sizes : [''];
    
        setProductFields(prevFields => ({
            ...prevFields,
            labelName: newLabelName,
            sizes: newSizes,
        }));
    };

    const getProducts = () => {
        const sortedData = ALL_DATA.sort((a, b) => {
            if (a.labelName && b.labelName) {
                return a.labelName.localeCompare(b.labelName);
            }
            return 0;
        });
        //setProducts(sortedData);
        setSearchedProducts(sortedData);
    }

    const postProduct = async () => {
        const productData = {
            ...productFields,
            category: category,
            id: generateUUID()
        };

        try {
            await firebaseService.postDataToFireStore('prices', productData);
            toast.success('New Product Added. Reload the page');
        } catch (error) {
            console.error("Error uploading user data: ", error);
        }
    }

    const deleteProduct = async (id: string) => {
        await firebaseService.deleteDataFromFireStore('prices', id);
        toast.success('Product deleted. Reload the page');
    }

    const deleteAllProducts = () => {
        axios.delete(`http://localhost:10000/api/deleteAllProducts`)
        .then(response => {
        })
        .catch(error => {
            console.error('Error:', error);
        });
    }

    const translatedProducts = products.map(product => {
        return {
            ...product,
            translatedName: product.labelName && product.labelName.includes('|A')
                ? translateText(product.labelName)
                : product.labelName || '',
        };
    });

    React.useEffect(() => {
        getProducts();     
    }, [triggerReload]);

    const search = React.useCallback(
        debounce((query) => {
            const lowerCaseQuery = query.toLowerCase().trim();
            const terms = lowerCaseQuery.split(' ').filter(term => term !== '');

            const filteredProducts = translatedProducts.filter((product) => {
                const productName = product?.translatedName?.toLowerCase() || '';
                const productBrand = product?.brand?.toLowerCase() || '';

                if (terms.length > 1) {
                    return terms.every(term => 
                        productName.includes(term) || productBrand.includes(term)
                    ) && productName.includes(terms[0]) && productBrand.includes(terms[1]);
                } else {
                    return productName.includes(lowerCaseQuery) || productBrand.includes(lowerCaseQuery);
                }
            });

            setSearchedProducts(filteredProducts);
        }, 1000),
        [searchedProducts, setInputValue, setProducts]
    );

    const resetSearch = () => {
        setSearchedProducts(searchedProducts);
        setInputValue('');
    };

    return (
        <ProductContext.Provider value={{ 
            products,
            setProducts,
            search,
            resetSearch,
            searchedProducts,
            setSearchedProducts,
            inputValue,
            setInputValue,
            productFields,
            category,
            handleSelectChange,
            handleInputChange,
            postProduct,
            deleteAllProducts,
            deleteProduct,
            translatedProducts,
            isLoading,
            setIsLoading
        }}>
            {children}
        </ProductContext.Provider>
    );
};